import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { auth, db, storage } from '@/lib/firebase';
import { signOut } from 'firebase/auth';
import { collection, getDocs, addDoc, serverTimestamp, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { sanitizeInput, sanitizeObject } from '@/lib/sanitize';
import { 
  Users, 
  LayoutDashboard, 
  Menu, 
  X, 
  Search, 
  Bell, 
  ChevronDown, 
  ChevronRight,
  Edit, 
  Trash2, 
  MoreVertical,
  LogOut,
  Settings,
  Tags,
  Plus,
  FolderOpen,
  Folder,
  Package,
  Loader2,
  Mail,
  MessageSquare
} from 'lucide-react';

interface UserData {
  id: string;
  email: string;
  role: string;
  createdAt?: any;
}

interface CategoryData {
  id: string;
  name: string;
  parentId: string | null;
  createdAt?: any;
}

interface Product {
  id?: string;
  category: string;
  categoryId: string;
  categoryName: string;
  title: string;
  desc: string;
  price: string;
  image: string;
  cta: string;
  link: string;
  badge?: string;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState('usuarios');
  const [usersList, setUsersList] = useState<UserData[]>([]);
  const [categoriesList, setCategoriesList] = useState<CategoryData[]>([]);
  const [productsList, setProductsList] = useState<Product[]>([]);
  const [messagesList, setMessagesList] = useState<any[]>([]);
  const [newsletterList, setNewsletterList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingSubTo, setAddingSubTo] = useState<string | null>(null);

  // Estados para creación de productos
  const [showProductForm, setShowProductForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [newProduct, setNewProduct] = useState<Omit<Product, 'id'>>({
    category: 'courses',
    categoryId: 'courses',
    categoryName: 'Cursos',
    title: '',
    desc: '',
    price: '',
    image: 'https://readdy.ai/api/search-image?query=digital%20product%20neon%20glow&width=400&height=280',
    cta: 'Comprar Ahora',
    link: 'https://stan.store',
    badge: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch Users
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as UserData[];
        setUsersList(users);

        // Fetch Categories
        const catsSnapshot = await getDocs(collection(db, 'categories'));
        const categories = catsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as CategoryData[];
        setCategoriesList(categories);

        // Fetch Products
        const productsSnapshot = await getDocs(collection(db, 'products'));
        const products = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Product[];
        setProductsList(products);

        // Fetch Messages
        const msgsSnapshot = await getDocs(collection(db, 'contact_messages'));
        const msgs = msgsSnapshot.docs.map(doc => ({ id: doc.id, status: 'pendiente', ...doc.data() }));
        setMessagesList(msgs);

        // Fetch Newsletter
        const newsSnapshot = await getDocs(collection(db, 'newsletter_subscribers'));
        const news = newsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setNewsletterList(news);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLogout = () => {
    signOut(auth);
  };

  const handleUpdateRole = async (userId: string, newRole: string) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole
      });
      setUsersList(usersList.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Error al actualizar el rol.');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) return;
    try {
      await deleteDoc(doc(db, 'users', userId));
      setUsersList(usersList.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error al eliminar usuario.');
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta categoría?')) return;
    try {
      await deleteDoc(doc(db, 'categories', id));
      setCategoriesList(categoriesList.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error al eliminar categoría.');
    }
  };

  const handleEditCategory = async (id: string, currentName: string) => {
    const newName = window.prompt('Nuevo nombre para la categoría:', currentName);
    if (!newName || newName === currentName) return;
    try {
      await updateDoc(doc(db, 'categories', id), { name: newName });
      setCategoriesList(categoriesList.map(c => c.id === id ? { ...c, name: newName } : c));
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este producto?')) return;
    try {
      await deleteDoc(doc(db, 'products', id));
      setProductsList(productsList.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error al eliminar producto.');
    }
  };

  const [editingProductId, setEditingProductId] = useState<string | null>(null);

  const handleEditProductClick = (product: Product) => {
    setNewProduct(product);
    setEditingProductId(product.id || null);
    setShowProductForm(true);
  };

  const handleUpdateMessageStatus = async (msgId: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, 'contact_messages', msgId), {
        status: newStatus
      });
      setMessagesList(messagesList.map(m => m.id === msgId ? { ...m, status: newStatus } : m));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error al actualizar el estado.');
    }
  };

  const compressImage = (file: File, maxWidth = 800, maxHeight = 800, quality = 0.7): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas is empty'));
            }
          }, 'image/jpeg', quality);
        };
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleAddCategory = async (parentId: string | null = null) => {
    if (!newCategoryName.trim()) return;

    const sanitizedName = sanitizeInput(newCategoryName);

    try {
      const docRef = await addDoc(collection(db, 'categories'), {
        name: sanitizedName,
        parentId: parentId,
        createdAt: serverTimestamp()
      });

      const newCat: CategoryData = {
        id: docRef.id,
        name: sanitizedName,
        parentId: parentId
      };
      
      setCategoriesList([...categoriesList, newCat]);
      setNewCategoryName('');
      setAddingSubTo(null);
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };

  const handleCreateProduct = async () => {
    setUploading(true);
    try {
      let imageUrl = newProduct.image;
      
      // Si hay un archivo seleccionado, lo comprimimos y subimos
      if (imageFile) {
        if (!imageFile.type.startsWith('image/')) {
          alert('Error: El archivo seleccionado no es una imagen válida.');
          setUploading(false);
          return;
        }

        // Eliminar imagen anterior si estamos editando
        if (editingProductId) {
          const oldProduct = productsList.find(p => p.id === editingProductId);
          if (oldProduct && oldProduct.image && oldProduct.image.includes('firebasestorage')) {
            try {
              const oldImageRef = ref(storage, oldProduct.image);
              await deleteObject(oldImageRef);
            } catch (e) {
              console.error('Error deleting old image:', e);
            }
          }
        }

        // Comprimir imagen
        const compressedBlob = await compressImage(imageFile);

        // Cambiar nombre y subir
        const storageRef = ref(storage, `products/${Date.now()}.jpg`);
        const snapshot = await uploadBytes(storageRef, compressedBlob);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      // Sanitizar datos del producto
      const sanitizedProduct = sanitizeObject(newProduct);

      // Encontrar el nombre de la categoría seleccionada
      const selectedCat = categoriesList.find(c => c.id === newProduct.categoryId);
      
      const productToSave = {
        ...sanitizedProduct,
        image: imageUrl,
        category: newProduct.categoryId, // Usamos el ID como categoría para el filtro
        categoryName: selectedCat ? selectedCat.name : 'Sin Categoría'
      };
      
      if (editingProductId) {
        await updateDoc(doc(db, 'products', editingProductId), productToSave);
        setProductsList(productsList.map(p => p.id === editingProductId ? { id: editingProductId, ...productToSave } : p));
        setEditingProductId(null);
      } else {
        const docRef = await addDoc(collection(db, 'products'), {
          ...productToSave,
          createdAt: serverTimestamp()
        });
        setProductsList([...productsList, { id: docRef.id, ...productToSave }]);
      }

      setShowProductForm(false);
      setImageFile(null);
      setNewProduct({
        category: 'courses',
        categoryId: 'courses',
        categoryName: 'Cursos',
        title: '',
        desc: '',
        price: '',
        image: 'https://readdy.ai/api/search-image?query=digital%20product%20neon%20glow&width=400&height=280',
        cta: 'Comprar Ahora',
        link: 'https://stan.store',
        badge: ''
      });
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error al guardar el producto.');
    } finally {
      setUploading(false);
    }
  };

  const getChildren = (parentId: string | null) => {
    return categoriesList.filter(c => c.parentId === parentId);
  };

  // Componente Recursivo para el Árbol de Categorías
  const CategoryNode = ({ category, level = 0 }: { category: CategoryData, level: number }) => {
    const children = getChildren(category.id);
    const [expanded, setExpanded] = useState(true);

    return (
      <div className={`${level > 0 ? 'ml-6 border-l border-slate-700/50 pl-4' : ''} mt-2`}>
        <div className="flex items-center justify-between gap-2 p-3 bg-slate-800/30 hover:bg-slate-700/30 rounded-lg group transition-colors">
          <div className="flex items-center gap-2">
            {children.length > 0 ? (
              <button onClick={() => setExpanded(!expanded)} className="text-slate-400 hover:text-white">
                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            ) : (
              <span className="w-4"></span>
            )}
            {children.length > 0 ? <FolderOpen size={16} className="text-cyan-400" /> : <Folder size={16} className="text-slate-500" />}
            <span className="font-medium text-slate-200">{category.name}</span>
          </div>
          
          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button 
              onClick={() => setAddingSubTo(category.id)}
              className="text-xs px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded hover:bg-emerald-500/20 transition-colors flex items-center gap-1"
            >
              <Plus size={12} />
              Sub
            </button>
            <button 
              onClick={() => handleEditCategory(category.id, category.name)}
              className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-cyan-400"
              title="Editar Nombre"
            >
              <Edit size={14} />
            </button>
            <button 
              onClick={() => handleDeleteCategory(category.id)}
              className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-red-400"
              title="Eliminar Categoría"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {addingSubTo === category.id && (
          <div className="ml-6 mt-2 flex gap-2">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder={`Nueva subcategoría en ${category.name}...`}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-emerald-500 w-full"
            />
            <button 
              onClick={() => handleAddCategory(category.id)}
              className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-medium text-sm transition-colors"
            >
              Añadir
            </button>
            <button 
              onClick={() => {setAddingSubTo(null); setNewCategoryName('');}}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium text-sm transition-colors"
            >
              Cancelar
            </button>
          </div>
        )}

        {expanded && children.length > 0 && (
          <div className="mt-1">
            {children.map(child => (
              <CategoryNode key={child.id} category={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white font-sans flex">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-800/90 backdrop-blur-xl border-r border-slate-700/50 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } flex flex-col`}
      >
        <div className="p-6 border-b border-slate-700/50 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Smart CRM
            </span>
          </div>
          <button className="text-slate-400 hover:text-white" onClick={() => setSidebarOpen(false)}>
            <X size={24} />
          </button>
        </div>
        
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <a
            href="/"
            className="w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors bg-purple-600/20 text-purple-400 border border-purple-500/20 hover:bg-purple-600/30 hover:text-white"
          >
            <Edit size={20} />
            Editar Front Site
          </a>
          <button
            onClick={() => setActiveMenu('dashboard')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'dashboard' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <LayoutDashboard size={20} />
            Dashboard
          </button>
          <button
            onClick={() => setActiveMenu('usuarios')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'usuarios' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <Users size={20} />
            Usuarios
          </button>
          <button
            onClick={() => setActiveMenu('categorias')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'categorias' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <Tags size={20} />
            Categorías
          </button>
          <button
            onClick={() => setActiveMenu('productos')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'productos' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <Package size={20} />
            Productos
          </button>
           <button
            onClick={() => setActiveMenu('mensajes')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'mensajes' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <MessageSquare size={20} />
            Mensajes
          </button>
          <button
            onClick={() => setActiveMenu('newsletter')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-colors ${
              activeMenu === 'newsletter' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <Mail size={20} />
            Newsletter
          </button>
          <button className="w-full flex items-center gap-3 p-3 rounded-lg font-medium text-slate-400 hover:bg-slate-700/50 hover:text-white transition-colors">
            <Settings size={20} />
            Configuración
          </button>
        </nav>
        

      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)}></div>
      )}

      {/* Main Content */}
      <div className={`flex-1 flex flex-col min-h-screen overflow-hidden transition-all duration-300 ${sidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        {/* Header */}
        <header className="bg-slate-800/50 backdrop-blur-md p-4 border-b border-slate-700/50 flex justify-between items-center">
          <button className="text-slate-400 hover:text-white" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Menu size={24} />
          </button>
          
          <div className="hidden md:flex items-center gap-2 bg-slate-700/50 px-3 py-2 rounded-lg border border-slate-600/50 w-64">
            <Search size={18} className="text-slate-400" />
            <input type="text" placeholder="Buscar..." className="bg-transparent border-none focus:outline-none text-sm text-white placeholder-slate-400 w-full" />
          </div>
          
          <div className="flex items-center gap-4">
            <button className="relative text-slate-400 hover:text-white">
              <Bell size={20} />
              <span className="absolute -top-1 -right-1 bg-emerald-500 w-2 h-2 rounded-full"></span>
            </button>
            
            <div 
              className="flex items-center gap-3 border-l border-slate-700/50 pl-4 cursor-pointer group"
              onClick={handleLogout}
              title="Cerrar Sesión"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center font-bold text-slate-900 text-sm">
                U
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-white">Admin</p>
                <p className="text-xs text-slate-400 truncate max-w-[150px]">{user?.email}</p>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-400 group-hover:text-red-400 transition-colors">
                <LogOut size={14} />
                <span>Logout</span>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 p-6 overflow-y-auto">
          {activeMenu === 'dashboard' && (
            <div>
              <h2 className="text-3xl font-bold mb-6">Overview</h2>
              <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700">
                <p className="text-slate-400">Aquí irían las métricas generales.</p>
              </div>
            </div>
          )}

          {activeMenu === 'usuarios' && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-3xl font-bold">Gestión de Usuarios</h2>
                  <p className="text-slate-400 mt-1">Lista de todos los usuarios registrados en el sistema.</p>
                </div>
              </div>

              {/* Table */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-700/30 text-slate-300 text-sm border-b border-slate-700/50">
                        <th className="p-4 font-semibold">Usuario</th>
                        <th className="p-4 font-semibold">Rol</th>
                        <th className="p-4 font-semibold">Estado</th>
                        <th className="p-4 font-semibold">ID</th>
                        <th className="p-4 font-semibold text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-slate-400">Cargando...</td>
                        </tr>
                      ) : (
                        usersList.map((u) => (
                          <tr key={u.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors text-slate-300">
                            <td className="p-4 flex items-center gap-3">
                              <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center text-sm font-bold text-slate-900">
                                {u.email.substring(0, 1).toUpperCase()}
                              </div>
                              <p className="font-medium text-white">{u.email}</p>
                            </td>
                            <td className="p-4">
                              <select
                                value={u.role}
                                onChange={(e) => handleUpdateRole(u.id, e.target.value)}
                                className="bg-slate-700/50 border border-slate-600 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-emerald-500 cursor-pointer"
                              >
                                <option value="client">client</option>
                                <option value="employee">employee</option>
                                <option value="admin">admin</option>
                              </select>
                            </td>
                            <td className="p-4">
                              <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                                Activo
                              </span>
                            </td>
                            <td className="p-4 font-mono text-xs text-slate-500">{u.id.substring(0, 8)}...</td>
                            <td className="p-4 flex justify-center gap-2">
                              <button 
                                onClick={() => handleDeleteUser(u.id)}
                                className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-red-400"
                                title="Eliminar Usuario"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'categorias' && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-3xl font-bold">Gestión de Categorías</h2>
                  <p className="text-slate-400 mt-1">Crea categorías y subcategorías ilimitadas hacia abajo.</p>
                </div>
              </div>

              {/* Formulario Raíz */}
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50 mb-6 flex gap-2">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Nueva categoría principal..."
                  className="bg-slate-700/50 border border-slate-600 focus:border-emerald-500 rounded-lg px-4 py-2 text-white focus:outline-none w-full"
                />
                <button 
                  onClick={() => handleAddCategory(null)}
                  className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap"
                >
                  <Plus size={18} />
                  Añadir Raíz
                </button>
              </div>

              {/* Árbol de Categorías */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 p-6 shadow-2xl">
                {categoriesList.filter(c => c.parentId === null).length === 0 ? (
                  <p className="text-slate-400 text-center py-4">No hay categorías. Crea la primera arriba.</p>
                ) : (
                  categoriesList.filter(c => c.parentId === null).map(rootCat => (
                    <CategoryNode key={rootCat.id} category={rootCat} level={0} />
                  ))
                )}
              </div>
            </div>
          )}

          {activeMenu === 'productos' && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-3xl font-bold">Gestión de Productos</h2>
                  <p className="text-slate-400 mt-1">Agrega y administra los productos de tu tienda.</p>
                </div>
                <button 
                  onClick={() => setShowProductForm(!showProductForm)}
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  {showProductForm ? <X size={18} /> : <Plus size={18} />}
                  {showProductForm ? "Cancelar" : "Nuevo Producto"}
                </button>
              </div>

              {/* Formulario de Creación */}
              {showProductForm && (
                <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700/50 mb-6 shadow-2xl">
                  <h3 className="text-xl font-bold mb-4">Crear Nuevo Producto</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Categoría (Desde Firestore)</label>
                      <select 
                        value={newProduct.categoryId}
                        onChange={(e) => setNewProduct({...newProduct, categoryId: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                      >
                        <option value="">Selecciona una categoría</option>
                        {categoriesList.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Título</label>
                      <input 
                        type="text" 
                        value={newProduct.title}
                        onChange={(e) => setNewProduct({...newProduct, title: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                        placeholder="Ej: Curso de Instagram"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-slate-400 text-xs block mb-1">Descripción</label>
                      <textarea 
                        value={newProduct.desc}
                        onChange={(e) => setNewProduct({...newProduct, desc: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500 h-20"
                        placeholder="Describe el producto..."
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Precio</label>
                      <input 
                        type="text" 
                        value={newProduct.price}
                        onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                        placeholder="Ej: $97"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Badge (Opcional)</label>
                      <input 
                        type="text" 
                        value={newProduct.badge}
                        onChange={(e) => setNewProduct({...newProduct, badge: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                        placeholder="Ej: Más Vendido"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Subir Imagen</label>
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={(e) => setImageFile(e.target.files ? e.target.files[0] : null)}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Link de Compra</label>
                      <input 
                        type="text" 
                        value={newProduct.link}
                        onChange={(e) => setNewProduct({...newProduct, link: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                        placeholder="Ej: https://stan.store/tuusuario"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <button 
                      onClick={() => setShowProductForm(false)}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium transition-colors"
                      disabled={uploading}
                    >
                      Cancelar
                    </button>
                    <button 
                      onClick={handleCreateProduct}
                      className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-medium transition-colors flex items-center gap-2"
                      disabled={uploading}
                    >
                      {uploading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Subiendo...
                        </>
                      ) : (
                        "Guardar Producto"
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Table of Products */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-700/30 text-slate-300 text-sm border-b border-slate-700/50">
                        <th className="p-4 font-semibold">Producto</th>
                        <th className="p-4 font-semibold">Categoría</th>
                        <th className="p-4 font-semibold">Precio</th>
                        <th className="p-4 font-semibold text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={4} className="p-8 text-center text-slate-400">Cargando...</td>
                        </tr>
                      ) : productsList.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="p-8 text-center text-slate-400">No hay productos. Crea el primero arriba.</td>
                        </tr>
                      ) : (
                        productsList.map((p) => (
                          <tr key={p.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors text-slate-300">
                            <td className="p-4 flex items-center gap-3">
                              <div className="w-10 h-10 bg-slate-700 rounded-lg overflow-hidden flex-shrink-0">
                                <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                              </div>
                              <div>
                                <p className="font-medium text-white">{p.title}</p>
                                <p className="text-xs text-slate-500 truncate max-w-[200px]">{p.desc}</p>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="text-xs px-2.5 py-1 rounded-full font-medium border bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
                                {p.categoryName}
                              </span>
                            </td>
                            <td className="p-4 font-bold text-emerald-400">{p.price}</td>
                            <td className="p-4 flex justify-center gap-2">
                              <button 
                                onClick={() => handleEditProductClick(p)}
                                className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-cyan-400"
                                title="Editar Producto"
                              >
                                <Edit size={16} />
                              </button>
                              <button 
                                onClick={() => handleDeleteProduct(p.id!)}
                                className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-red-400"
                                title="Eliminar Producto"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'mensajes' && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-3xl font-bold">Mensajes de Contacto</h2>
                  <p className="text-slate-400 mt-1">Gestiona los mensajes recibidos desde el sitio web.</p>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-700/30 text-slate-300 text-sm border-b border-slate-700/50">
                        <th className="p-4 font-semibold">Nombre</th>
                        <th className="p-4 font-semibold">Email</th>
                        <th className="p-4 font-semibold">Mensaje</th>
                        <th className="p-4 font-semibold">Estado</th>
                        <th className="p-4 font-semibold">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-slate-400">Cargando...</td>
                        </tr>
                      ) : messagesList.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-slate-400">No hay mensajes.</td>
                        </tr>
                      ) : (
                        messagesList.map((m) => (
                          <tr key={m.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors text-slate-300">
                            <td className="p-4 font-medium text-white">{m.name}</td>
                            <td className="p-4">{m.email}</td>
                            <td className="p-4 text-xs max-w-[300px] truncate" title={m.message}>{m.message}</td>
                            <td className="p-4">
                              <select
                                value={m.status || 'pendiente'}
                                onChange={(e) => handleUpdateMessageStatus(m.id, e.target.value)}
                                className="bg-slate-700/50 border border-slate-600 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-emerald-500 cursor-pointer"
                              >
                                <option value="pendiente">Pendiente</option>
                                <option value="en curso">En Curso</option>
                                <option value="cancelado">Cancelado</option>
                                <option value="finalizado">Finalizado</option>
                              </select>
                            </td>
                            <td className="p-4 font-mono text-xs text-slate-500">{m.ip || 'N/A'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'newsletter' && (
            <div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-3xl font-bold">Suscriptores de Newsletter</h2>
                  <p className="text-slate-400 mt-1">Lista de correos registrados para novedades.</p>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl border border-slate-700/50 overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-700/30 text-slate-300 text-sm border-b border-slate-700/50">
                        <th className="p-4 font-semibold">Email</th>
                        <th className="p-4 font-semibold">IP</th>
                        <th className="p-4 font-semibold">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={3} className="p-8 text-center text-slate-400">Cargando...</td>
                        </tr>
                      ) : newsletterList.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="p-8 text-center text-slate-400">No hay suscriptores.</td>
                        </tr>
                      ) : (
                        newsletterList.map((n) => (
                          <tr key={n.id} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors text-slate-300">
                            <td className="p-4 font-medium text-white">{n.email}</td>
                            <td className="p-4 font-mono text-xs text-slate-500">{n.ip || 'N/A'}</td>
                            <td className="p-4 text-xs text-slate-400">
                              {n.createdAt?.seconds ? new Date(n.createdAt.seconds * 1000).toLocaleString() : 'N/A'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
